import { useMemo, useState } from 'react';

import { format, parseISO } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';

import type { StripeTransactionSummary } from '@/types/service/internalAnalytics';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { formatCurrency } from '@/utils/saasDashboardHelpers';

interface StripeTransactionsTableProps {
  transactions: StripeTransactionSummary[];
}

type SortField =
  | 'transaction_date'
  | 'correct_statement_descriptor'
  | 'correct_amount'
  | 'fee'
  | 'fee_deducted_correct_amount'
  | 'status'
  | 'customer_email';
type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  field: SortField | null;
  direction: SortDirection;
}

const StripeTransactionsTable = ({ transactions }: StripeTransactionsTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  const formatDate = (isoDate: string) => {
    try {
      return format(parseISO(isoDate), 'MMM dd, yyyy');
    } catch {
      return isoDate;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'default';
      case 'Failed':
        return 'destructive';
      case 'Refunded':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const handleSort = (field: SortField) => {
    setSortState((prev) => {
      if (prev.field !== field) {
        return { field, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { field, direction: 'desc' };
      }
      if (prev.direction === 'desc') {
        return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) {
      return <ArrowUpDown className='ml-1 h-4 w-4 text-muted-foreground' />;
    }
    if (sortState.direction === 'asc') {
      return <ArrowUp className='ml-1 h-4 w-4' />;
    }
    return <ArrowDown className='ml-1 h-4 w-4' />;
  };

  const filteredAndSortedTransactions = useMemo(() => {
    let result = [...transactions];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((txn) => {
        return (
          txn.correct_statement_descriptor?.toLowerCase().includes(query) ||
          txn.customer_email?.toLowerCase().includes(query) ||
          txn.status.toLowerCase().includes(query) ||
          txn.stripe_charge_id?.toLowerCase().includes(query) ||
          formatDate(txn.transaction_date).toLowerCase().includes(query)
        );
      });
    }

    // Sort
    if (sortState.field && sortState.direction) {
      result.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;

        switch (sortState.field) {
          case 'transaction_date':
            aVal = a.transaction_date;
            bVal = b.transaction_date;
            break;
          case 'correct_statement_descriptor':
            aVal = a.correct_statement_descriptor || '';
            bVal = b.correct_statement_descriptor || '';
            break;
          case 'correct_amount':
            aVal = parseFloat(a.correct_amount);
            bVal = parseFloat(b.correct_amount);
            break;
          case 'fee':
            aVal = parseFloat(a.fee);
            bVal = parseFloat(b.fee);
            break;
          case 'fee_deducted_correct_amount':
            aVal = parseFloat(a.fee_deducted_correct_amount);
            bVal = parseFloat(b.fee_deducted_correct_amount);
            break;
          case 'status':
            aVal = a.status;
            bVal = b.status;
            break;
          case 'customer_email':
            aVal = a.customer_email || '';
            bVal = b.customer_email || '';
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortState.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const comparison = String(aVal).localeCompare(String(bVal));
        return sortState.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [transactions, searchQuery, sortState]);

  if (transactions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[200px] flex items-center justify-center text-muted-foreground'>
            No transactions found
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <CardTitle className='text-lg'>
            Transactions
            <span className='ml-2 text-sm font-normal text-muted-foreground'>
              ({filteredAndSortedTransactions.length} of {transactions.length})
            </span>
          </CardTitle>
          <div className='relative w-full sm:w-64'>
            <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              type='text'
              placeholder='Search transactions...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-8'
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className='h-[400px]'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none'
                  onClick={() => handleSort('transaction_date')}
                >
                  <div className='flex items-center'>
                    Date
                    {getSortIcon('transaction_date')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none'
                  onClick={() => handleSort('correct_statement_descriptor')}
                >
                  <div className='flex items-center'>
                    Descriptor
                    {getSortIcon('correct_statement_descriptor')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none text-right'
                  onClick={() => handleSort('correct_amount')}
                >
                  <div className='flex items-center justify-end'>
                    Gross
                    {getSortIcon('correct_amount')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none text-right'
                  onClick={() => handleSort('fee')}
                >
                  <div className='flex items-center justify-end'>
                    Fee
                    {getSortIcon('fee')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none text-right'
                  onClick={() => handleSort('fee_deducted_correct_amount')}
                >
                  <div className='flex items-center justify-end'>
                    Net
                    {getSortIcon('fee_deducted_correct_amount')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none'
                  onClick={() => handleSort('status')}
                >
                  <div className='flex items-center'>
                    Status
                    {getSortIcon('status')}
                  </div>
                </TableHead>
                <TableHead
                  className='cursor-pointer hover:bg-muted/50 select-none'
                  onClick={() => handleSort('customer_email')}
                >
                  <div className='flex items-center'>
                    Customer
                    {getSortIcon('customer_email')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className='h-24 text-center text-muted-foreground'>
                    No transactions match your search.
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedTransactions.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell className='whitespace-nowrap'>
                      {formatDate(txn.transaction_date)}
                    </TableCell>
                    <TableCell
                      className='max-w-[150px] truncate'
                      title={txn.correct_statement_descriptor || ''}
                    >
                      {txn.correct_statement_descriptor || '-'}
                    </TableCell>
                    <TableCell className='text-right whitespace-nowrap'>
                      {formatCurrency(parseFloat(txn.correct_amount))}
                    </TableCell>
                    <TableCell className='text-right whitespace-nowrap'>
                      {formatCurrency(parseFloat(txn.fee))}
                    </TableCell>
                    <TableCell className='text-right whitespace-nowrap font-medium'>
                      {formatCurrency(parseFloat(txn.fee_deducted_correct_amount))}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(txn.status)}>{txn.status}</Badge>
                    </TableCell>
                    <TableCell className='max-w-[180px] truncate' title={txn.customer_email || ''}>
                      {txn.customer_email || '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default StripeTransactionsTable;
